import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import OVERVIEW_MAIN from '../../../../app/assets/images/projectImage/cryoneimage/overview_lpatop.webp'
import OVERVIEW_COIN from '../../../../app/assets/images/projectImage/cryoneimage/coin_final.png'

type CryoneProjectOverviewProps = {
    cryoneProject: any
}

const CryoneProjectOverview = ({ cryoneProject }: CryoneProjectOverviewProps) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: false, amount: 0.2 })
    const { cryoneProjectDetails } = cryoneProject ?? {}
    const { overviewTabs, websiteDetails, overviewSection } = cryoneProjectDetails ?? {}

    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] }
        }
    }

    const slideLeft = {
        hidden: { opacity: 0, x: -40 },
        visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] }
        }
    }

    const slideRight = {
        hidden: { opacity: 0, x: 40 },
        visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] }
        }
    }

    const popIn = {
        hidden: { opacity: 0, scale: 0.94 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
        }
    }

    return (
        <section
            className="relative bg-[#141414] text-white py-16 sm:py-20"
            style={{ overflow: 'hidden', isolation: 'isolate' }}
            ref={sectionRef}
        >
{/* ══ LEFT DIAGONAL GLOW ══ */}
<div
    aria-hidden="true"
    style={{
        position: 'absolute',
        left: '-220px',
        top: '85%',
        transform: 'translateY(-50%) rotate(-35deg) scaleX(2) scaleY(0.6)',
        width: '380px',
        height: '380px',
        borderRadius: '50%',
        background:
            'radial-gradient(circle at center, rgba(255,60,60,0.8) 0%, rgba(200,30,30,0.35) 35%, rgba(120,10,10,0.1) 55%, transparent 70%)',
        filter: 'blur(60px)',
        opacity: 0.85,
        zIndex: 0,
        pointerEvents: 'none',
    }}
/>

{/* ══ RIGHT DIAGONAL GLOW ══ */}
<div
    aria-hidden="true"
    style={{
        position: 'absolute',
        right: '-220px',
        top: '85%',
        transform: 'translateY(-50%) rotate(35deg) scaleX(2) scaleY(0.6)',
        width: '380px',
        height: '380px',
        borderRadius: '50%',
        background:
            'radial-gradient(circle at center, rgba(255,60,60,0.8) 0%, rgba(200,30,30,0.35) 35%, rgba(120,10,10,0.1) 55%, transparent 70%)',
        filter: 'blur(60px)',
        opacity: 0.85,
        zIndex: 0,
        pointerEvents: 'none',
    }}
/>

            {/* ══ CONTENT ══ */}
            <div className="relative z-10 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-12">
                {/* ── TABS (UI) ── */}
                <motion.div
  className="flex justify-center mb-6 md:mb-12"
  variants={fadeUp}
  initial="hidden"
  animate={isInView ? 'visible' : 'hidden'}
>
  <div className="w-full max-w-[1200px]">
    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">

      {/* Left Tab */}
      <div className="w-full md:w-[30%] h-[60px] md:h-[68px] rounded-[20px] border border-[#B41F1F]
        bg-[radial-gradient(circle_at_20%_50%,rgba(180,31,31,0.2),transparent_48%),linear-gradient(180deg,#241111_0%,#150D0D_100%)]
        shadow-[0_14px_30px_rgba(0,0,0,0.28)]
        flex items-center justify-center"
      >
        <span className="text-[#F1DFDF] text-lg md:text-xl xl:text-[32px] font-medium text-center px-4">
          {overviewTabs[0]?.title}
        </span>
      </div>

      {/* Center Active Tab */}
      <div className="w-full md:w-[36%] h-[70px] md:h-[76px]
        rounded-[20px] border border-[#DA3A31]
        bg-[linear-gradient(180deg,#DD3429_0%,#B41F1F_100%)]
        shadow-[0_16px_34px_rgba(180,31,31,0.42)]
        flex items-center justify-center
        md:-rotate-[7deg] z-10 md:-mx-4"
      >
        <span className="text-[#F7EAEA] text-xl md:text-2xl xl:text-[39px] font-medium text-center px-4">
          {overviewTabs[1]?.title}
        </span>
      </div>

      {/* Right Tab */}
      <div className="w-full md:w-[30%] h-[60px] md:h-[68px] rounded-[20px] border border-[#B41F1F]
        bg-[radial-gradient(circle_at_20%_50%,rgba(180,31,31,0.2),transparent_48%),linear-gradient(180deg,#241111_0%,#150D0D_100%)]
        shadow-[0_14px_30px_rgba(0,0,0,0.28)]
        flex items-center justify-center"
      >
        <span className="text-[#F1DFDF] text-lg md:text-xl xl:text-[32px] font-medium text-center px-4">
          {overviewTabs[2]?.title}
        </span>
      </div>

    </div>
  </div>
</motion.div>

                {/* ── 4 CARDS (UI) ── */}
                <motion.div
    className="flex justify-center mb-8"
    variants={popIn}
    initial="hidden"
    animate={isInView ? 'visible' : 'hidden'}
>
    <div className="w-full max-w-[1200px] pb-10 sm:pb-12 lg:pb-16 px-3 sm:px-6 lg:px-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
            {websiteDetails?.map((card: any, index: number) => (
                <div
                    key={card.key || card.title}
                    className={`rounded-xl p-[1px] bg-[linear-gradient(127.18deg,#B41F1F_0%,#441717_50%,#B41F1F_100%)]
                        ${index === 1 || index === 2 ? 'lg:translate-y-14' : ''}
                    `}
                >
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#1A0D0D] via-[#130C0C] to-[#0F0B0B] p-3 sm:p-4 lg:p-5 shadow-[0_10px_24px_rgba(0,0,0,0.35)] min-h-[120px] sm:min-h-[150px] lg:min-h-[130px]">
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(180,31,31,0.25),transparent_60%)]" />
                        <div className="relative z-10">
                            <p className="text-[10px] sm:text-[12px] lg:text-[13px] text-white/95 mb-2 font-semibold leading-tight">
                                {card.title}:
                            </p>
                            <ul className={`space-y-1 ${index === 1 || index === 2 ? 'list-disc pl-3 lg:pl-4' : ''}`}>
                                {card.items?.map((item: string) => (
                                    <li
                                        key={item}
                                        className="text-[9px] sm:text-[11px] lg:text-[13px] text-white/80 leading-snug break-words"
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
</motion.div>

                {/* ── OVERVIEW TITLE — coin to the LEFT of text ── */}
                <motion.div
                    className="relative flex items-center justify-center gap-3 sm:gap-4 mt-10 sm:mt-16 lg:mt-40"
                    variants={fadeUp}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                >
                    {/* Coin: in-flow on mobile (left of title), floating on desktop */}
                    <motion.img
                        src={OVERVIEW_COIN}
                        alt="Cryone token"
                        className="pointer-events-none absolute sm:hidden w-[52px] left-1/2 top-1/2 -translate-y-[42%] -ml-[180px]"
                        animate={isInView ? { y: [0, -12, 0], rotate: [0, -6, 0] } : {}}
                        transition={{ duration: 3.8, repeat: Infinity, ease: 'easeInOut' }}
                    />

                    <motion.img
                        src={OVERVIEW_COIN}
                        alt="Cryone token"
                        className="pointer-events-none absolute hidden sm:block w-[72px] lg:w-[148px] left-1/2 top-1/2 -translate-y-[60%] -translate-x-[250%] lg:left-auto lg:right-[calc(50%+250px)] lg:top-[-55px] lg:translate-x-0 lg:-translate-y-0"
                        animate={isInView ? { y: [0, -12, 0], rotate: [0, -6, 0] } : {}}
                        transition={{ duration: 3.8, repeat: Infinity, ease: 'easeInOut' }}
                    />

                    <span
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: '#B41F1F',
                            flexShrink: 0,
                        }}
                    />
                    <h2
                        style={{
                            fontSize: 'clamp(42px, 6vw, 80px)',
                            fontWeight: 800,
                            letterSpacing: '1.5px',
                            textTransform: 'uppercase',
                            lineHeight: 1,
                            margin: 0,
                        }}
                    >
                        {overviewSection?.title}
                    </h2>
                    <span
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: '#B41F1F',
                            flexShrink: 0,
                        }}
                    />
                </motion.div>

                {/* ── OVERVIEW BODY ── */}
                <div style={{ position: 'relative', marginTop: '56px' }}>
                    <div
                        className="relative z-[1] grid grid-cols-1 lg:grid-cols-[1fr_1.6fr_1fr] gap-8 lg:gap-12 items-center"
                    >
                        {/* left paragraph */}
                        <motion.p
                            style={{
                                fontSize: 'clamp(15px, 1.3vw, 21px)',
                                lineHeight: 1.6,
                                color: 'rgba(255,255,255,0.90)',
                                margin: 0,
                            }}
                            className="text-center lg:text-left max-w-[520px] mx-auto lg:mx-0"
                            variants={slideLeft}
                            initial="hidden"
                            animate={isInView ? 'visible' : 'hidden'}
                        >
                            {overviewSection?.leftDescription}
                        </motion.p>

                        {/* centre laptop mockup */}
                        <motion.div
                            style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}
                            variants={popIn}
                            initial="hidden"
                            animate={isInView ? 'visible' : 'hidden'}
                        >
                            <motion.img
                                src={OVERVIEW_MAIN}
                                alt="Cryone dashboard preview"
                                style={{
                                    width: '100%',
                                    maxWidth: '820px',
                                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.55))',
                                }}
                                whileHover={{ y: -4, transition: { duration: 0.25 } }}
                            />
                            
                        </motion.div>

                        {/* right paragraph */}
                        <motion.p
                            style={{
                                fontSize: 'clamp(15px, 1.3vw, 21px)',
                                lineHeight: 1.6,
                                color: 'rgba(255,255,255,0.90)',
                                margin: 0,
                            }}
                            className="text-center lg:text-right max-w-[520px] mx-auto lg:mx-0"
                            variants={slideRight}
                            initial="hidden"
                            animate={isInView ? 'visible' : 'hidden'}
                        >
                            {overviewSection?.rightDescription}
                        </motion.p>
                    </div>

                    {/* bottom sentence */}
                    <motion.p
                        style={{
                            marginTop: '56px',
                            textAlign: 'center',
                            fontSize: 'clamp(17px, 1.8vw, 30px)',
                            lineHeight: 1.45,
                            color: '#fff',
                        }}
                        variants={fadeUp}
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                    >
                        {overviewSection?.footerDescription}
                    </motion.p>
                </div>
            </div>
        </section>
    )
}

export default CryoneProjectOverview
